import React, { useState } from 'react';
import { Heart, Download, ZoomIn, ZoomOut, Grid, List, CheckCircle, MessageSquare, X, Maximize2 } from 'lucide-react';

interface LogoVariant {
  id: number;
  file_name: string;
  metadata?: any;
}

interface LogoContestPreviewProps {
  assets: LogoVariant[];
  apiBaseUrl: string;
  onVote?: (assetId: number) => Promise<void>;
  onComment?: (assetId: number, comment: string) => Promise<void>;
}

interface Comment {
  id: string;
  text: string;
  timestamp: Date;
}

export default function LogoContestPreview({ assets, apiBaseUrl, onVote, onComment }: LogoContestPreviewProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedLogo, setSelectedLogo] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);
  const [votes, setVotes] = useState<Record<number, boolean>>({});
  const [favorites, setFavorites] = useState<Record<number, boolean>>({});
  const [logoComments, setLogoComments] = useState<Record<number, Comment[]>>({});
  const [newComment, setNewComment] = useState('');
  const [lightboxAsset, setLightboxAsset] = useState<LogoVariant | null>(null);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));

  const handleVote = async (assetId: number) => {
    if (onVote) {
      await onVote(assetId);
    }
    setVotes(prev => ({ ...prev, [assetId]: !prev[assetId] }));
  };

  const handleFavorite = (assetId: number) => {
    setFavorites(prev => ({ ...prev, [assetId]: !prev[assetId] }));
  };

  const handleAddComment = () => {
    if (!selectedLogo || !newComment.trim()) return;

    const comment: Comment = {
      id: Date.now().toString(),
      text: newComment.trim(),
      timestamp: new Date()
    };

    setLogoComments(prev => ({
      ...prev,
      [selectedLogo]: [...(prev[selectedLogo] || []), comment]
    }));

    if (onComment) {
      onComment(selectedLogo, newComment.trim());
    }

    setNewComment('');
  };

  const handleDeleteComment = (assetId: number, commentId: string) => {
    setLogoComments(prev => ({
      ...prev,
      [assetId]: (prev[assetId] || []).filter(c => c.id !== commentId)
    }));
  };

  const handleDownload = (asset: LogoVariant) => {
    const url = getAssetUrl(asset);
    const link = document.createElement('a');
    link.href = url;
    link.download = asset.file_name;
    link.click();
  };

  const getAssetUrl = (asset: LogoVariant) => {
    return `${apiBaseUrl}/assets/${asset.id}/download`;
  };

  const selectedAsset = selectedLogo ? assets.find(a => a.id === selectedLogo) : null;
  const votedCount = Object.values(votes).filter(Boolean).length;
  const favoritesCount = Object.values(favorites).filter(Boolean).length;

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="flex-none bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Logo Contest Review</h3>
            <p className="text-sm text-gray-500 mt-1">
              Review {assets.length} logo design{assets.length !== 1 ? 's' : ''} •
              {votedCount > 0 && ` ${votedCount} selected`}
              {favoritesCount > 0 && ` • ${favoritesCount} favorite${favoritesCount !== 1 ? 's' : ''}`}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                title="Grid View"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="px-3 text-sm font-medium text-gray-700 min-w-[60px] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 3}
                className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Logo List */}
        <div className="flex-1 overflow-y-auto p-6">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => setSelectedLogo(asset.id)}
                  className={`relative bg-white rounded-lg border-2 cursor-pointer transition-all ${
                    selectedLogo === asset.id
                      ? 'border-blue-500 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  {/* Badges */}
                  <div className="absolute top-2 right-2 flex gap-1 z-10">
                    {votes[asset.id] && (
                      <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Selected
                      </span>
                    )}
                    {favorites[asset.id] && (
                      <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">
                        ❤️
                      </span>
                    )}
                  </div>

                  {/* Image */}
                  <div className="p-8 flex items-center justify-center bg-gray-50 rounded-t-lg" style={{ minHeight: '200px' }}>
                    <img
                      src={getAssetUrl(asset)}
                      alt={asset.file_name}
                      style={{ transform: `scale(${zoom})`, maxWidth: '100%', maxHeight: '200px' }}
                      className="object-contain transition-transform"
                    />
                  </div>

                  {/* Info & Actions */}
                  <div className="p-3 border-t">
                    <p className="text-sm text-gray-600 mb-2 truncate">{asset.file_name}</p>
                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFavorite(asset.id);
                        }}
                        className={`p-1.5 rounded ${
                          favorites[asset.id] ? 'text-red-500' : 'text-gray-400 hover:text-red-400'
                        }`}
                        title="Favorite"
                      >
                        <Heart className={`w-4 h-4 ${favorites[asset.id] ? 'fill-current' : ''}`} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(asset);
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-500 rounded"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setLightboxAsset(asset);
                        }}
                        className="p-1.5 text-gray-400 hover:text-purple-500 rounded"
                        title="Fullscreen"
                      >
                        <Maximize2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => setSelectedLogo(asset.id)}
                  className={`bg-white rounded-lg border-2 cursor-pointer transition-all ${
                    selectedLogo === asset.id
                      ? 'border-blue-500 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center gap-4 p-4">
                    {/* Thumbnail */}
                    <div className="flex-none w-24 h-24 bg-gray-50 rounded flex items-center justify-center p-2">
                      <img
                        src={getAssetUrl(asset)}
                        alt={asset.file_name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {votes[asset.id] && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                        {favorites[asset.id] && (
                          <Heart className="w-4 h-4 text-red-500 fill-current" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{asset.file_name}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVote(asset.id);
                        }}
                        className={`px-4 py-2 rounded ${
                          votes[asset.id]
                            ? 'bg-green-100 text-green-700 border border-green-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {votes[asset.id] ? 'Selected' : 'Select'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(asset);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-500 rounded"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Sidebar - Comments for Selected Logo */}
        {selectedAsset && (
          <div className="w-96 bg-white border-l overflow-y-auto">
            <div className="p-6">
              {/* Large Preview with Lightbox */}
              <div className="mb-6 p-8 bg-gray-50 rounded-lg relative group">
                <img
                  src={getAssetUrl(selectedAsset)}
                  alt={selectedAsset.file_name}
                  className="w-full h-auto cursor-pointer"
                  onClick={() => setLightboxAsset(selectedAsset)}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                  <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>

              <h4 className="font-medium text-gray-900 mb-2">{selectedAsset.file_name}</h4>

              {/* Vote & Favorite Buttons */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => handleVote(selectedAsset.id)}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                    votes[selectedAsset.id]
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {votes[selectedAsset.id] ? '✓ Selected' : 'Select This'}
                </button>
                <button
                  onClick={() => handleFavorite(selectedAsset.id)}
                  className={`px-4 py-2 rounded-lg ${
                    favorites[selectedAsset.id]
                      ? 'bg-red-100 text-red-600'
                      : 'bg-gray-100 text-gray-400 hover:text-red-400'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${favorites[selectedAsset.id] ? 'fill-current' : ''}`} />
                </button>
              </div>

              {/* Comments Section */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Comments
                </h4>
                <div className="space-y-3 mb-4">
                  {(logoComments[selectedAsset.id] || []).map((comment) => (
                    <div key={comment.id} className="bg-gray-50 rounded p-3 relative group">
                      <p className="text-sm text-gray-700 pr-6">{comment.text}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {comment.timestamp.toLocaleString()}
                      </p>
                      <button
                        onClick={() => handleDeleteComment(selectedAsset.id, comment.id)}
                        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add Comment */}
                <div className="space-y-2">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add your feedback..."
                    className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Add Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxAsset && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxAsset(null)}
        >
          <button
            onClick={() => setLightboxAsset(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={getAssetUrl(lightboxAsset)}
            alt={lightboxAsset.file_name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg">
            {lightboxAsset.file_name}
          </div>
        </div>
      )}
    </div>
  );
}
